/**
 * 首页组件
 * 从原 ShowcaseWebsite 的 renderMainPage 提取
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Carousel, Col, Row, Typography } from 'antd';
import { newsService } from '../../../../services/newsService';
import { policyApi, PolicyItem } from '../../../../services/policyService';
import { PlatformInfo, platformService } from '../../../../services/platformService';
import ActivityFloatingBox from '../../../../components/common/ActivityFloatingBox';
import { useCommonConfigStore } from '../../../../store/commonConfigStore';
import { NewsItem as News } from '../../../../types';
import '../ShowcaseWebsite.css';

// 扩展News类型
interface ExtendedNews extends News {
    category?: string;
    publishTime?: string;
    coverImage?: string;
    summary?: string;
}

const { Title } = Typography;

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { contactInfo } = useCommonConfigStore();
    const [activeNewsTab, setActiveNewsTab] = useState<'work-dynamics' | 'industry-news'>('work-dynamics');
    const [showActivityFloat, setShowActivityFloat] = useState(true);
    
    // 数据状态
    const [news, setNews] = useState<ExtendedNews[]>([]);
    const [policies, setPolicies] = useState<PolicyItem[]>([]);
    const [platformInfo, setPlatformInfo] = useState<PlatformInfo | null>(null);
    const [loading, setLoading] = useState(false);

    // 加载数据 - 仅在首页路径时才加载
    useEffect(() => {
        // 检查当前路径是否为首页
        const isHomePage = location.pathname === '/show/main' || 
                          location.pathname === '/show' || 
                          location.pathname === '/show/' ||
                          location.pathname === '/main';
        
        if (!isHomePage) {
            // 如果不是首页，不加载数据
            return;
        }
        
        const loadData = async () => {
            setLoading(true);
            try {
                const [newsRes, policyRes, platformRes] = await Promise.all([
                    newsService.getList({ current: 1, size: 1000, status: '1' }),
                    policyApi.getList({ current: 1, size: 1000 }),
                    platformService.getInfo()
                ]);

                if (newsRes.code === 200) {
                    setNews((newsRes.data?.records || newsRes.data || []) as ExtendedNews[]);
                }
                if (policyRes.code === 200) {
                    setPolicies(policyRes.data?.records || policyRes.data || []);
                }
                if (platformRes.code === 200 && platformRes.data) {
                    setPlatformInfo(platformRes.data);
                }
            } catch (error) {
                console.error('加载数据失败:', error);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [location.pathname]);

    // 获取活动预告数据
    const activityData = useMemo(() => {
        const activityItems = news
            .filter(item => (item as ExtendedNews).category === 'activity_announcement' || (item as ExtendedNews).category === '活动预告' || item.type === '活动预告')
            .sort((a, b) => {
                const timeA = new Date((a as ExtendedNews).publishTime || a.date || 0).getTime();
                const timeB = new Date((b as ExtendedNews).publishTime || b.date || 0).getTime();
                return timeB - timeA;
            });
        const firstItem = activityItems[0];
        if (!firstItem) return undefined;
        
        // 转换为ActivityFloatingBox期望的格式
        return {
            id: firstItem.id,
            title: firstItem.title || '',
            publishTime: (firstItem as ExtendedNews).publishTime || firstItem.date || ''
        };
    }, [news]);

    // 获取分类新闻数据
    const getTopNewsByCategory = (categoryKey: 'work-dynamics' | 'industry-news') => {
        const categoryMap = {
            'work-dynamics': ['工作动态', 'work_dynamics'],
            'industry-news': ['行业新闻', 'industry_news']
        };

        const matched = news.filter(item => {
            const categories = categoryMap[categoryKey];
            return categories.some(cat => 
                (item as ExtendedNews).category?.includes(cat) || item.type?.includes(cat)
            );
        });

        return matched.sort((a, b) => {
            const timeA = new Date((a as ExtendedNews).publishTime || 0).getTime();
            const timeB = new Date((b as ExtendedNews).publishTime || 0).getTime();
            return timeB - timeA;
        }).slice(0, 6);
    };

    const categoryNews = getTopNewsByCategory(activeNewsTab);
    const workDynamicsData = getTopNewsByCategory('work-dynamics');
    const industryNewsData = getTopNewsByCategory('industry-news');

    // 通知公告数据
    const noticeItems = useMemo(() => {
        return news
            .filter(item => (item as ExtendedNews).category === 'notice' || (item as ExtendedNews).category === '通知公告' || item.type === '通知公告')
            .sort((a, b) => {
                const timeA = new Date((a as ExtendedNews).publishTime || 0).getTime();
                const timeB = new Date((b as ExtendedNews).publishTime || 0).getTime();
                return timeB - timeA;
            })
            .slice(0, 5);
    }, [news]);

    // 政策法规数据
    const policyItems = useMemo(() => {
        return policies
            .sort((a, b) => (b.issuanceDate ? new Date(b.issuanceDate).getTime() : 0) - (a.issuanceDate ? new Date(a.issuanceDate).getTime() : 0))
            .slice(0, 5);
    }, [policies]);

    return (
        <div className="main-page">
            {/* 活动预告浮动框 */}
            {showActivityFloat && (
                <ActivityFloatingBox
                    visible={showActivityFloat}
                    onClose={() => setShowActivityFloat(false)}
                    activityData={activityData}
                />
            )}

            {/* 工作动态 - 统一容器布局 */}
            <div className="main-content-container">
                <div className="main-content-wrapper">
                    {/* 左侧轮播图 */}
                    <div className="left-content-box">
                        <div className="carousel-box">
                            {categoryNews && categoryNews.length > 0 ? (
                                <Carousel autoplay effect="fade" className="news-carousel">
                                    {categoryNews.map((item) => {
                                        let coverImageUrl = (item as ExtendedNews).coverImage;
                                        if (coverImageUrl && !coverImageUrl.startsWith('data:') && !coverImageUrl.startsWith('http')) {
                                            if (!coverImageUrl.startsWith('/')) {
                                                coverImageUrl = '/' + coverImageUrl;
                                            }
                                        }
                                        if (!coverImageUrl) {
                                            coverImageUrl = '/images/carousel/1.png';
                                        }

                                        return (
                                            <div key={item.id} className="carousel-item">
                                                <div
                                                    className="carousel-image"
                                                    style={{
                                                        backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${coverImageUrl})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center'
                                                    }}
                                                    onClick={() => {
                                                        if (item.id < 999000) {
                                                            navigate(`/show/news/${item.id}`);
                                                        }
                                                    }}
                                                >
                                                    <div className="carousel-caption">
                                                        <h3 className="carousel-title">{item.title}</h3>
                                                        <p className="carousel-description">{(item as ExtendedNews).summary || ''}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </Carousel>
                            ) : (
                                <Carousel autoplay effect="fade" className="news-carousel">
                                    <div className="carousel-item">
                                        <div
                                            className="carousel-image"
                                            style={{
                                                backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url('/images/carousel/1.png')`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center'
                                            }}
                                        >
                                            <div className="carousel-caption">
                                                <h3 className="carousel-title">知识产权服务收费标准公示</h3>
                                                <p className="carousel-description">知识产权服务收费标准公示,接受社会监督。</p>
                                            </div>
                                        </div>
                                    </div>
                                </Carousel>
                            )}
                        </div>
                    </div>

                    {/* 右侧新闻列表 */}
                    <div className="right-content-box">
                        <div className="news-box">
                            <div className="news-header">
                                <div className="news-tabs">
                                    <div
                                        className={`news-tab ${activeNewsTab === 'work-dynamics' ? 'active' : ''}`}
                                        onClick={() => setActiveNewsTab('work-dynamics')}
                                    >
                                        工作动态
                                    </div>
                                    <div
                                        className={`news-tab ${activeNewsTab === 'industry-news' ? 'active' : ''}`}
                                        onClick={() => setActiveNewsTab('industry-news')}
                                    >
                                        行业新闻
                                    </div>
                                </div>
                                <Button type="link" className="more-link"
                                    onClick={() => navigate(`/show/news?tab=${activeNewsTab}`)}>
                                    更多
                                </Button>
                            </div>

                            <div className="news-content-box">
                                <div className="news-content">
                                    {activeNewsTab === 'work-dynamics' ? (
                                        <>
                                            {workDynamicsData[0] && (
                                                <div className="featured-news" onClick={() => {
                                                    if (workDynamicsData[0].id < 999000) {
                                                        navigate(`/show/news/${workDynamicsData[0].id}`);
                                                    }
                                                }}>
                                                    <h3 className="featured-title">{workDynamicsData[0].title}</h3>
                                                </div>
                                            )}

                                            <div className="news-list">
                                                {workDynamicsData.slice(1).map((item) => (
                                                    <div key={item.id} className="news-item"
                                                        onClick={() => {
                                                            if (item.id < 999000) {
                                                                navigate(`/show/news/${item.id}`);
                                                            }
                                                        }}>
                                                        <span className="news-title">{item.title}</span>
                                                        <span className="news-date">{(item as ExtendedNews).publishTime || item.date || ''}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {industryNewsData[0] && (
                                                <div className="featured-news" onClick={() => {
                                                    if (industryNewsData[0].id < 999000) {
                                                        navigate(`/show/news/${industryNewsData[0].id}`);
                                                    }
                                                }}>
                                                    <h3 className="featured-title">{industryNewsData[0].title}</h3>
                                                </div>
                                            )}

                                            <div className="news-list">
                                                {industryNewsData.slice(1).map((item) => (
                                                    <div key={item.id} className="news-item"
                                                        onClick={() => {
                                                            if (item.id < 999000) {
                                                                navigate(`/show/news/${item.id}`);
                                                            }
                                                        }}>
                                                        <span className="news-title">{item.title}</span>
                                                        <span className="news-date">{(item as ExtendedNews).publishTime || item.date || ''}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 四宫格布局 */}
            <div className="four-grid-section">
                <Row gutter={[16, 16]}>
                    {/* 通知公告 */}
                    <Col xs={24} sm={12}>
                        <div className="grid-item">
                            <div className="grid-header">
                                <Title level={4}>通知公告</Title>
                                <Button type="link" className="more-link"
                                    onClick={() => navigate('/show/news?tab=notices')}>更多</Button>
                            </div>
                            <div className="grid-content">
                                {noticeItems.length === 0 ? (
                                    <div className="list-item" style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                                        暂无通知公告数据
                                    </div>
                                ) : (
                                    noticeItems.map(item => (
                                        <div key={item.id} className="list-item" onClick={() => navigate(`/show/notice/${item.id}`)}>
                                            <span className="item-title">{item.title}</span>
                                            <span className="item-date">{(item as ExtendedNews).publishTime || item.date || ''}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* 政策法规 */}
                    <Col xs={24} sm={12}>
                        <div className="grid-item">
                            <div className="grid-header">
                                <Title level={4}>政策法规</Title>
                                <Button type="link" className="more-link"
                                    onClick={() => navigate('/show/policy')}>更多</Button>
                            </div>
                            <div className="grid-content">
                                {policyItems.length === 0 ? (
                                    <div className="list-item" style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                                        暂无政策法规数据
                                    </div>
                                ) : (
                                    policyItems.map(item => (
                                        <div key={item.id} className="list-item" onClick={() => {
                                            if (item.webLink) {
                                                window.open(item.webLink, '_blank');
                                            } else {
                                                navigate(`/show/policy/${item.id}`);
                                            }
                                        }}>
                                            <span className="item-title">{item.fileName}</span>
                                            <span className="item-date">{item.issuanceDate || '暂无日期'}</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </Col>

                    {/* 平台简介 */}
                    <Col xs={24} sm={12}>
                        <div className="grid-item">
                            <div className="grid-header">
                                <Title level={4}>平台简介</Title>
                            </div>
                            <div className="grid-content">
                                <div className="intro-text">
                                    {platformInfo ? (
                                        <div dangerouslySetInnerHTML={{ __html: platformInfo.content || '' }} />
                                    ) : (
                                        <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
                                            暂无平台简介数据
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Col>

                    {/* 联系我们 */}
                    <Col xs={24} sm={12}>
                        <div className="grid-item">
                            <div className="grid-header">
                                <Title level={4}>联系我们</Title>
                            </div>
                            <div className="grid-content">
                                <div className="contact-info">
                                    {contactInfo.officePhone && (
                                        <div className="contact-item">
                                            <span className="contact-label">办公室：</span>
                                            <span className="contact-value">{contactInfo.officePhone}</span>
                                        </div>
                                    )}
                                    {contactInfo.preAuditPhone && (
                                        <div className="contact-item">
                                            <span className="contact-label">预审部：</span>
                                            <span className="contact-value">{contactInfo.preAuditPhone}</span>
                                        </div>
                                    )}
                                    {contactInfo.contactAddress && (
                                        <div className="contact-item">
                                            <span className="contact-label">地址：</span>
                                            <span className="contact-value">{contactInfo.contactAddress}</span>
                                        </div>
                                    )}
                                    {contactInfo.postalCode && (
                                        <div className="contact-item">
                                            <span className="contact-label">邮编：</span>
                                            <span className="contact-value">{contactInfo.postalCode}</span>
                                        </div>
                                    )}
                                    {contactInfo.contactEmail && (
                                        <div className="contact-item">
                                            <span className="contact-label">邮箱：</span>
                                            <span className="contact-value">{contactInfo.contactEmail}</span>
                                        </div>
                                    )}
                                    {!contactInfo.officePhone && !contactInfo.preAuditPhone && !contactInfo.contactAddress && (
                                        <div className="contact-item">
                                            <span className="contact-label">办公室：</span>
                                            <span className="contact-value">0714-1234567</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </div>
    );
};

export default HomePage;

